import Redux from 'redux';
import * as Bixby from '$/core';
import {
  PortfolioState,
  PortfolioActionTypes,
  PORTFOLIO_LOADING,
  PORTFOLIO_SUCCESS,
  PORTFOLIO_FAILURE,
  COMPANY_LOADING,
  COMPANY_SUCCESS,
  COMPANY_FAILURE,
  COMPANY_FINANCIAL_LOADING,
  COMPANY_FINANCIAL_SUCCESS,
  COMPANY_FINANCIAL_FAILURE,
  SET_ALERTS_ENABLED_SUCCESS,
  SET_ALERTS_ENABLED_FAILURE,
  SAVE_CLIENT_EBITDA_LOADING,
  SAVE_CLIENT_EBITDA_SUCCESS,
  SAVE_CLIENT_EBITDA_FAILURE,
  CREATE_DEAL_NOTE_LOADING,
  CREATE_DEAL_NOTE_SUCCESS,
  CREATE_DEAL_NOTE_FAILURE,
  DEAL_NOTES_LOADING,
  DEAL_NOTES_SUCCESS,
  DEAL_NOTES_FAILURE,
} from './types';
import { LOGIN_CLEAR_PRELOADED_DATA } from '$/store/login/types';

const resetFinancialStatements: () => Bixby.Models.FinancialStatement[] = () => {
  return [
    {
      isDraft: false,
      financialStatementType: Bixby.Enums.FinancialStatementType.BalanceSheet,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: false,
      financialStatementType: Bixby.Enums.FinancialStatementType.CashFlow,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: false,
      financialStatementType: Bixby.Enums.FinancialStatementType.IncomeStatement,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: false,
      financialStatementType: Bixby.Enums.FinancialStatementType.KPIs,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: false,
      financialStatementType: Bixby.Enums.FinancialStatementType.SummaryFinancials,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: true,
      financialStatementType: Bixby.Enums.FinancialStatementType.BalanceSheet,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: true,
      financialStatementType: Bixby.Enums.FinancialStatementType.CashFlow,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: true,
      financialStatementType: Bixby.Enums.FinancialStatementType.IncomeStatement,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: true,
      financialStatementType: Bixby.Enums.FinancialStatementType.KPIs,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
    {
      isDraft: true,
      financialStatementType: Bixby.Enums.FinancialStatementType.SummaryFinancials,
      isLoading: false,
      data: null,
    } as Bixby.Models.FinancialStatement,
  ];
};

const initialState: PortfolioState = {
  isLoading: false,
  isSavingClientEbitda: false,
  portfolio: undefined,
  error: undefined,
  currentCompany: undefined,
  financialStatements: resetFinancialStatements(),
  isCreatingDealNote: false,
  isLoadingDealNotes: false,
  dealNotes: undefined,
};

const portfolioReducer: Redux.Reducer<PortfolioState> = (
  state = initialState,
  action: PortfolioActionTypes
): PortfolioState => {
  switch (action.type) {
    case LOGIN_CLEAR_PRELOADED_DATA:
      return initialState;
    case PORTFOLIO_LOADING:
      return {
        ...state,
        isLoading: true,
        error: undefined,
      };
    case PORTFOLIO_SUCCESS:
      return {
        ...state,
        isLoading: false,
        portfolio: action.payload as Bixby.Models.CompanyPortfolio[],
        currentCompany: undefined,
        error: undefined,
      };
    case PORTFOLIO_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload as Bixby.Models.BixbyError,
        portfolio: undefined,
      };
    case COMPANY_LOADING:
      return {
        ...state,
        isLoading: true,
        error: undefined,
      };
    case COMPANY_SUCCESS:
      const companyPayload = action.payload as Bixby.Models.Company;
      return {
        ...state,
        isLoading: false,
        currentCompany: companyPayload,
        financialStatements:
          (state.currentCompany || ({} as Bixby.Models.Company)).id !== companyPayload.id
            ? resetFinancialStatements()
            : state.financialStatements,
        error: undefined,
      };
    case COMPANY_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload as Bixby.Models.BixbyError,
        currentCompany: undefined,
      };
    case COMPANY_FINANCIAL_LOADING:
      const financialsPayloadLoading = action.payload as Bixby.Models.FinancialStatementLoading;
      return {
        ...state,
        isLoading: true,
        financialStatements: state.financialStatements.map((item: Bixby.Models.FinancialStatement) => {
          if (
            item.financialStatementType === financialsPayloadLoading.financialStatementType &&
            item.isDraft === financialsPayloadLoading.isDraft
          ) {
            return {
              ...item,
              isLoading: true,
            } as Bixby.Models.FinancialStatement;
          }
          return item;
        }),
        error: undefined,
      };
    // TODO - remove using isLoading for financials and instead use the inner loading property on the financial statement model
    case COMPANY_FINANCIAL_SUCCESS:
      const financialsPayload = action.payload as Bixby.Models.FinancialStatement;
      return {
        ...state,
        isLoading: false,
        financialStatements: state.financialStatements.map((item: Bixby.Models.FinancialStatement) => {
          if (
            item.financialStatementType === financialsPayload.financialStatementType &&
            item.isDraft === financialsPayload.isDraft
          ) {
            return {
              ...item,
              isLoading: false,
              data: financialsPayload.data,
            } as Bixby.Models.FinancialStatement;
          }
          return item;
        }),
        error: undefined,
      };
    case COMPANY_FINANCIAL_FAILURE:
      const financialsPayloadError = action.payload as Bixby.Models.FinancialStatementError;
      return {
        ...state,
        isLoading: false,
        financialStatements: state.financialStatements.map((item: Bixby.Models.FinancialStatement) => {
          if (
            item.financialStatementType === financialsPayloadError.financialStatementType &&
            item.isDraft === financialsPayloadError.isDraft
          ) {
            return {
              ...item,
              isLoading: false,
            } as Bixby.Models.FinancialStatement;
          }
          return item;
        }),
        error: financialsPayloadError.error as Bixby.Models.BixbyError,
      };
    case SET_ALERTS_ENABLED_SUCCESS:
      const companyId: string = action.payload as any as string;
      const currentCompany: Bixby.Models.Company | undefined = state.currentCompany;
      const newCompanyState: Bixby.Models.Company = {} as any;
      if (currentCompany && currentCompany.id === companyId) {
        newCompanyState.alertsEnabled = !currentCompany.alertsEnabled;
      }

      return {
        ...state,
        currentCompany: !currentCompany ? undefined : Object.assign({}, currentCompany, newCompanyState),
        error: undefined,
      };
    case SET_ALERTS_ENABLED_FAILURE:
      return {
        ...state,
        error: (action.payload || {}) as Bixby.Models.BixbyError,
      };
    case SAVE_CLIENT_EBITDA_LOADING:
      return {
        ...state,
        isSavingClientEbitda: true,
        error: undefined,
      };
    case SAVE_CLIENT_EBITDA_SUCCESS:
      const companyUpdatedPayload = action.payload as Bixby.Models.Company;
      return {
        ...state,
        isSavingClientEbitda: false,
        currentCompany: companyUpdatedPayload,
        error: undefined,
      };
    case SAVE_CLIENT_EBITDA_FAILURE:
      return {
        ...state,
        isSavingClientEbitda: false,
        error: action.payload as Bixby.Models.BixbyError,
        currentCompany: undefined,
      };
    case CREATE_DEAL_NOTE_LOADING:
      return {
        ...state,
        isCreatingDealNote: true,
        error: undefined,
      };
    case CREATE_DEAL_NOTE_SUCCESS:
      const latestDealNotes = action.payload as Bixby.Models.DealNote[];
      return {
        ...state,
        isCreatingDealNote: false,
        dealNotes: latestDealNotes,
        error: undefined,
      };
    case CREATE_DEAL_NOTE_FAILURE:
      return {
        ...state,
        isCreatingDealNote: false,
        error: action.payload as Bixby.Models.BixbyError,
        dealNotes: undefined,
      };
    case DEAL_NOTES_LOADING:
      return {
        ...state,
        isLoadingDealNotes: true,
        error: undefined,
      };
    case DEAL_NOTES_SUCCESS:
      const dealNotesPayload = action.payload as Bixby.Models.DealNote[];
      return {
        ...state,
        isLoadingDealNotes: false,
        dealNotes: dealNotesPayload,
        error: undefined,
      };
    case DEAL_NOTES_FAILURE:
      return {
        ...state,
        isLoadingDealNotes: false,
        error: action.payload as Bixby.Models.BixbyError,
        dealNotes: undefined,
      };
    default:
      return state;
  }
};

export default portfolioReducer;
